/**
 * Modal
 *
 * @author rkalms <rkalms@vertic.com>
 * @author dkvistgaard <dkvistgaard@vertic.com>
 */

import _ from 'underscore';
import Backbone from 'backbone';
import EventEmitter from 'events';
import Handlebars from 'handlebars';

import BackboneView from './view';

// Views
import ModalView from '../core/views/modal-view';

const defaults = {
	className: 'modal--show',
	frame: '#modal-frame',

	// Optionally pass a different view to the CoreObject
	view: ModalView,
};

const documentListenerTypes = [];
const documentListeners = [];
function documentListener(event) {
	if (documentListenerTypes.indexOf(event.type) < 0) {
		return;
	}

	documentListeners.forEach((listener) => {
		if (event.target.matches ? event.target.matches(listener.selector) : event.target.msMatchesSelector(listener.selector)) {
			listener.instance.open();
		}
	});
}

class Instance extends EventEmitter {
	constructor(options) {
		super();

		this.options = options;
		this.modal = options.scope;
		this.className = options.className;
		this.force = options.force;

		this.setEventType();

		// Assign event if selector present
		// And if there are no selector, then we'll just open the modal view.
		if (options.selector) {
			documentListeners.push({
				selector: options.selector,
				instance: this,
			});

			if (documentListenerTypes.indexOf(this.eventType) < 0) {
				documentListenerTypes.push(this.eventType);
				document.documentElement.addEventListener(this.eventType, documentListener);
			}
		} else if (this.modal.layout.collection.length > 0) {
			// If there's another modal open, then we'll close that one before opening a new
			this.modal.layout.close(false, () => {
				this.open();
			});
		} else {
			this.open();
		}
	}

	/**
	 * Open Modal
	 *
	 * @param {Object} e (optional)
	 */
	open(e) {
		if (this.modal.layout.isClosing) return;
		if (e && e.type === 'touchend' && e.cancelable === false) return;

		// Stop if we're trying to reopen the same modal instance
		if (this.view && this.view.isOpen) return;

		// Instance view
		if (this.modal.layout.collection.length > 0) {
			const openView = this.modal.layout.collection.at(0);
			openView.isOpen = false;
		}

		if (e) e.preventDefault();

		clearTimeout(this.modal.layout.closeTimeout);

		let viewModel = typeof this.options.model === 'function' ? new this.options.model() : this.options.model;

		if (!(viewModel instanceof Backbone.Model)) {
			viewModel = new Backbone.Model(viewModel);
		}

		viewModel.set('hideClose', this.force);

		const viewOptions = {
			instance: this,
			type: this.options.type,
			parent: this.options.parent,
		};

		if (this.options.template) {
			const templateMarkup = this.options.template;
			this.options.view = this.options.view.extend({
				getTemplate() {
					return Handlebars.compile(templateMarkup);
				},
			});
		}

		this.view = this.options.view;

		this.modal.layout.addModal(viewModel, this.view, viewOptions);

		document.documentElement.classList.add('modal--open');
	}

	/**
	 * getEventType
	 * Checking if the current device is touch device or not.
	 *
	 * @return {Boolean}
	 */
	setEventType() {
		let check = false;
		((a) => { if (/(android|ipad|playbook|silk|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor || window.opera);

		this.eventType = check ? 'touchend' : 'click';
	}
}

class Modal {
	constructor(options) {
		this.modals = [];
		this.options = _.extend({}, defaults, options);

		if (!document.querySelector(this.options.frame)) {
			console.warn(`No modal frame found with the ${this.options.frame} selector!`);
			return;
		}

		const Layout = BackboneView.extend({
			el: this.options.frame,

			collection: new Backbone.Collection(),

			events: {
				'click .js-modal__close': 'close',
			},

			initialize() {
				this.collection.bind('add', this.add, this);
				this.collection.bind('reset', this.reset, this);

				document.body.addEventListener('keydown', this.keyHandler.bind(this));
			},

			addModal(model, View = ModalView, viewOptions = {}) {
				this.ItemView = View;
				this.viewOptions = viewOptions;

				this.collection.add(model);
			},

			add(model) {
				const itemView = new this.ItemView({
					model,
				});

				itemView.options = this.viewOptions;
				itemView.isOpen = true;

				this.View = itemView;

				const content = this.el.querySelector('.modal__content');
				content.appendChild(itemView.render().el);
			},

			reset() {
				const content = this.el.querySelector('.modal__content');
				content.innerHTML = '';
			},

			keyHandler(event) {
				if (!document.documentElement.classList.contains('modal--open')) {
					return;
				}

				if (event.which === 27 && !this.View.options.instance.force) {
					this.close();
				}
			},

			close(e, cb) {
				const contentView = this.View;

				if (this.isClosing || (e && contentView && contentView.options.instance.force)) {
					return;
				}

				if (e) {
					const isInner = e.target.classList.contains('modal__inner');
					const isClose = e.target.classList.contains('js-modal__close');
					const closestInner = Modal.closest(e.target, '.modal__inner');

					if ((isInner || closestInner) && !isClose) {
						return;
					}

					e.preventDefault();
				}

				clearTimeout(this.closeTimeout);
				this.isClosing = true;

				if (contentView && typeof contentView.onBeforeModalClose === 'function') {
					contentView.onBeforeModalClose();
				}

				const modalBody = this.el.querySelector('.modal__body');
				if (modalBody) {
					modalBody.classList.remove(this.options.modal.className);
				}

				document.documentElement.classList.remove('modal--open');


                // Free the body so it is able to scroll again
                const body = document.body;
                const scrollY = body.style.top;
                body.style.position = '';
                body.style.top = '';
                body.style.height = '';
                body.style.overflowY = '';
                window.scrollTo(0, parseInt(scrollY || '0') * -1);

				this.closeTimeout = setTimeout(() => {
					// Instance view
					if (contentView) {
						contentView.isOpen = false;
					}

					this.collection.reset();

					this.isClosing = false;
					this.View = null;

					if (cb && typeof cb === 'function') cb();
                }, 500);
			},

		});

		this.layout = new Layout();
		this.layout.options = {
			modal: this.options,
		};
	}

	create(options) {
		const settings = options.options || {};

		// Checking if the current modal is forced
		if (!this.layout || (this.layout.currentView && this.layout.currentView.options.instance.force)) {
			return;
		}

		const instance = _.extend(new Instance({
			selector: options.selector || false,
			force: options.force || false,
			view: options.view || this.options.view,
			model: options.model || Backbone.Model,
			className: this.options.className,
			type: options.type || 'default',
			template: options.template || false,
			parent: options.parent || false,
			scope: this,
		}), settings);

		this.modals.push(instance);

		// Events
		instance.on('close', (cb) => {
			const index = this.modals.indexOf(instance);

            this.layout.close();



            const body = document.body;
            console.log('body??', body)
                const scrollY = body.style.top;
                body.style.position = '';
                body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);



			if (cb && typeof cb === 'function') cb();

			this.modals.splice(index, 1);
		});

		const data = instance.options.model;
		this.populateModal(data);
	}

	populateModal(data) {
		const modal = this.layout.el.querySelector('.modal__inner');

		// inject hubspot form
		if (data.hubSpot) {
			const hubSpotScript = document.createElement('script');
			const hubSpotForm = `hbspt.forms.create({
				portalId: "${data.hubSpot.portalId}",
				formId: "${data.hubSpot.formId}"
			})`;

			hubSpotScript.appendChild(document.createTextNode(hubSpotForm));
			modal.appendChild(hubSpotScript);
			window.thankYouModal = data.hubSpot.thankYouModal;
		}
	}

	closeAll() {
		_.each(this.modals, (modal) => {
			if (modal.view && modal.view.isOpen) {
				modal.emit('close');
			}
		});
	}

	/**
	 * Finding the closest element in the parent tree.
	 *
	 * @param {HTMLElement} el
	 * @param {String} target
	 */
	static closest(el, target) {
		let ancestor = el;

		if (!document.documentElement.contains(el)) {
			return null;
		}

		do {
			if (ancestor.matches ? ancestor.matches(target) : ancestor.msMatchesSelector(target)) {
				return ancestor;
			}

			ancestor = ancestor.parentElement;
		} while (ancestor !== null);

		return null;
	}
}

export default Modal;
